document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const inEditor = Boolean(window.Shopify && window.Shopify.designMode);

function initReveal(root = document) {
  const items = root.querySelectorAll('[data-reveal]:not(.is-revealed)');
  if (!items.length) return;
  if (!document.body.classList.contains('reveal-on') || inEditor || reduceMotion.matches || !('IntersectionObserver' in window)) {
    items.forEach((item) => item.classList.add('is-revealed'));
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const group = entry.target.closest('[data-reveal-group]');
      const targets = group ? group.querySelectorAll('[data-reveal]') : [entry.target];
      targets.forEach((target) => {
        target.classList.add('is-revealed');
        observer.unobserve(target);
      });
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
  items.forEach((item) => observer.observe(item));
}

function initHeader() {
  const header = document.querySelector('[data-site-header]');
  const sentinel = document.querySelector('[data-header-sentinel]');
  if (!header || !sentinel || !('IntersectionObserver' in window)) return;
  new IntersectionObserver(([entry]) => {
    header.classList.toggle('is-compact', !entry.isIntersecting);
  }).observe(sentinel);
}

function initHeroHeader() {
  const header = document.querySelector('[data-site-header]');
  if (!header) return;
  const root = document.documentElement;
  const measure = () => {
    const bar = document.querySelector('.announcement-bar');
    root.style.setProperty('--announcement-h', `${bar ? bar.offsetHeight : 0}px`);
    if (!header.classList.contains('is-compact')) root.style.setProperty('--header-h', `${header.offsetHeight}px`);
  };
  measure();
  if (!root.dataset.heroHeaderReady) {
    root.dataset.heroHeaderReady = 'true';
    let timer;
    window.addEventListener('resize', () => {
      clearTimeout(timer);
      timer = setTimeout(measure, 150);
    }, { passive: true });
  }
}

function initHeaderHide() {
  const header = document.querySelector('[data-site-header][data-hide-on-scroll]');
  if (!header || inEditor || header.dataset.hideReady) return;
  header.dataset.hideReady = 'true';
  const root = document.documentElement;
  let lastY = window.scrollY;
  let ticking = false;
  const update = () => {
    ticking = false;
    const y = Math.max(0, window.scrollY);
    const delta = y - lastY;
    lastY = y;
    const keepShown = root.classList.contains('menu-open') || root.classList.contains('bag-open')
      || header.classList.contains('has-open-panel') || Boolean(header.querySelector(':focus-visible'));
    if (keepShown || y < header.offsetHeight + 120) {
      root.classList.remove('header-hidden');
      return;
    }
    if (delta > 4) root.classList.add('header-hidden');
    else if (delta < -4) root.classList.remove('header-hidden');
  };
  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }, { passive: true });
  header.addEventListener('focusin', () => root.classList.remove('header-hidden'));
}

function initMegaMenu(root = document) {
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  root.querySelectorAll('[data-mega]').forEach((item) => {
    if (item.dataset.ready) return;
    item.dataset.ready = 'true';
    const trigger = item.querySelector('[data-mega-trigger]');
    const panel = item.querySelector('[data-mega-panel]');
    const header = item.closest('[data-site-header]');
    if (!trigger || !panel) return;
    let timer;
    let loaded = false;
    let escaped = false;
    const loadImages = () => {
      if (loaded) return;
      loaded = true;
      panel.querySelectorAll('img[data-src]').forEach((img) => {
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
      });
    };
    const set = (open) => {
      if (open) loadImages();
      item.classList.toggle('is-open', open);
      trigger.setAttribute('aria-expanded', String(open));
      if (header) header.classList.toggle('has-open-panel', open);
    };
    item.addEventListener('mega:toggle', (event) => set(Boolean(event.detail)));
    item.addEventListener('mouseenter', () => {
      if (!finePointer.matches) return;
      clearTimeout(timer);
      timer = setTimeout(() => set(true), 90);
    });
    item.addEventListener('mouseleave', () => {
      clearTimeout(timer);
      timer = setTimeout(() => set(false), 180);
    });
    item.addEventListener('focusin', () => {
      clearTimeout(timer);
      if (escaped) {
        escaped = false;
        return;
      }
      set(true);
    });
    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget)) set(false);
    });
    item.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !item.classList.contains('is-open')) return;
      set(false);
      if (document.activeElement !== trigger) {
        escaped = true;
        trigger.focus();
      }
    });
    const figures = panel.querySelectorAll('[data-mega-figure]');
    panel.querySelectorAll('[data-mega-key]').forEach((link) => {
      const activate = () => figures.forEach((figure) => figure.classList.toggle('is-active', figure.dataset.megaFigure === link.dataset.megaKey));
      link.addEventListener('mouseenter', activate);
      link.addEventListener('focus', activate);
    });
  });
}

function initCardGalleries(root = document) {
  root.querySelectorAll('[data-card-gallery]').forEach((gallery) => {
    if (gallery.dataset.ready) return;
    gallery.dataset.ready = 'true';
    const track = gallery.querySelector('.card-gallery__track');
    const count = track ? track.children.length : 0;
    if (count < 2) return;
    const prev = gallery.querySelector('[data-gallery-prev]');
    const next = gallery.querySelector('[data-gallery-next]');
    const dots = gallery.querySelectorAll('.card-gallery__dot');
    let index = 0;
    let ticking = false;
    const update = () => {
      ticking = false;
      index = Math.max(0, Math.min(count - 1, Math.round(track.scrollLeft / (track.clientWidth || 1))));
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === count - 1;
    };
    const wake = () => {
      track.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    };
    const go = (to) => {
      wake();
      const target = Math.max(0, Math.min(count - 1, to));
      track.scrollTo({ left: target * track.clientWidth, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    };
    let settleTimer;
    let touching = false;
    const settle = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        if (touching) return;
        const width = track.clientWidth || 1;
        const nearest = Math.round(track.scrollLeft / width);
        if (Math.abs(track.scrollLeft - nearest * width) > 2) track.scrollTo({ left: nearest * width, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }, 160);
    };
    track.addEventListener('scroll', () => {
      settle();
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
    track.addEventListener('touchstart', () => {
      touching = true;
      clearTimeout(settleTimer);
    }, { passive: true });
    ['touchend', 'touchcancel'].forEach((type) => track.addEventListener(type, () => {
      touching = false;
      settle();
    }, { passive: true }));
    if (prev) prev.addEventListener('click', () => go(index - 1));
    if (next) next.addEventListener('click', () => go(index + 1));
    track.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      go(index + (event.key === 'ArrowRight' ? 1 : -1));
    });
    ['pointerenter', 'touchstart', 'focusin'].forEach((type) => gallery.addEventListener(type, wake, { once: true, passive: true }));
    window.addEventListener('resize', update, { passive: true });
    update();
  });
}

const progressItems = new Set();
let progressObserver;
let progressTicking = false;

function progressFor(element, rect, viewport) {
  const mode = element.dataset.scrollProgress;
  if (mode === 'through') return (viewport - rect.top) / (viewport + rect.height);
  if (mode === 'frame') return (viewport - rect.top) / (viewport * 0.85);
  return (viewport * 0.9 - rect.top) / (viewport * 0.55 + rect.height * 0.5);
}

function updateProgress() {
  progressTicking = false;
  const viewport = window.innerHeight;
  progressItems.forEach((element) => {
    const value = Math.min(1, Math.max(0, progressFor(element, element.getBoundingClientRect(), viewport)));
    element.style.setProperty('--p', value.toFixed(4));
  });
}

function requestProgress() {
  if (progressTicking) return;
  progressTicking = true;
  requestAnimationFrame(updateProgress);
}

function initScrollProgress(root = document) {
  const items = root.querySelectorAll('[data-scroll-progress]');
  if (!items.length) return;
  if (reduceMotion.matches || inEditor || !('IntersectionObserver' in window)) {
    items.forEach((element) => element.style.setProperty('--p', '1'));
    return;
  }
  if (!progressObserver) {
    progressObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          progressItems.add(entry.target);
        } else {
          progressItems.delete(entry.target);
          entry.target.style.setProperty('--p', entry.boundingClientRect.top < 0 ? '1' : '0');
        }
      });
      requestProgress();
    }, { rootMargin: '15% 0px 15% 0px' });
    window.addEventListener('scroll', requestProgress, { passive: true });
    window.addEventListener('resize', requestProgress, { passive: true });
  }
  items.forEach((element) => progressObserver.observe(element));
}

function initBreedIndex(root = document) {
  root.querySelectorAll('[data-breed-index]').forEach((index) => {
    if (index.dataset.ready) return;
    index.dataset.ready = 'true';
    const links = index.querySelectorAll('[data-breed-key]');
    const figures = index.querySelectorAll('[data-breed-figure]');
    const activate = (key) => {
      links.forEach((link) => link.classList.toggle('is-active', link.dataset.breedKey === key));
      figures.forEach((figure) => figure.classList.toggle('is-active', figure.dataset.breedFigure === key));
    };
    links.forEach((link) => {
      link.addEventListener('mouseenter', () => activate(link.dataset.breedKey));
      link.addEventListener('focus', () => activate(link.dataset.breedKey));
    });
  });
}

const ORDER_BAG_KEY = 'marlow-order-bag';
const ORDER_SENT_KEY = 'marlow-order-sent';

function readOrderBag() {
  try {
    const items = JSON.parse(window.localStorage.getItem(ORDER_BAG_KEY) || '[]');
    return Array.isArray(items) ? items.filter((item) => item && item.key && item.qty > 0) : [];
  } catch (error) {
    return [];
  }
}

function writeOrderBag(items) {
  try {
    window.localStorage.setItem(ORDER_BAG_KEY, JSON.stringify(items));
  } catch (error) {
    /* storage unavailable: the bag still works for this page view */
  }
}

function formatOrderMoney(cents, format) {
  const value = Math.round(Number(cents) || 0) / 100;
  const pattern = format || '£{{amount}}';
  return pattern.replace(/\{\{\s*(\w+)\s*\}\}/, (match, kind) => {
    if (kind.includes('no_decimals')) return Math.round(value).toLocaleString('en-GB');
    if (kind.includes('comma')) return value.toFixed(2).replace('.', ',');
    return value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  });
}

function initOrderBag() {
  const dialog = document.querySelector('[data-order-bag]');
  if (!dialog || dialog.dataset.ready) return;
  dialog.dataset.ready = 'true';
  document.body.classList.add('order-bag-on');
  const root = document.documentElement;
  const form = dialog.querySelector('form');
  const list = dialog.querySelector('[data-order-items]');
  const empty = dialog.querySelector('[data-order-empty]');
  const summary = dialog.querySelector('[data-order-summary]');
  const total = dialog.querySelector('[data-order-total]');
  const orderField = dialog.querySelector('[data-order-field]');
  const bodyField = dialog.querySelector('[data-order-body]');
  const status = dialog.querySelector('[data-order-status]');
  const main = dialog.querySelector('[data-order-main]');
  const success = dialog.querySelector('[data-order-success]');
  const errors = dialog.querySelector('[data-order-errors]');
  const format = dialog.dataset.moneyFormat;
  let items = readOrderBag();
  let returnFocus = null;

  const count = () => items.reduce((sum, item) => sum + item.qty, 0);
  const announce = (message) => {
    if (!status) return;
    status.textContent = '';
    requestAnimationFrame(() => { status.textContent = message; });
  };
  const updateBadges = () => {
    const quantity = count();
    document.querySelectorAll('.icon-link--cart').forEach((link) => {
      let badge = link.querySelector('[data-order-count]');
      link.setAttribute('aria-label', `Bag, ${quantity} ${quantity === 1 ? 'item' : 'items'}`);
      link.querySelectorAll('[data-bag-count]').forEach((label) => { label.textContent = `(${quantity})`; });
      if (!quantity) {
        if (badge) badge.remove();
        return;
      }
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'cart-badge';
        badge.setAttribute('data-order-count', '');
        badge.setAttribute('aria-hidden', 'true');
        link.appendChild(badge);
      }
      badge.textContent = String(quantity);
    });
  };
  const button = (label, action, key, text) => {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = `order-bag__qty-button order-bag__qty-button--${action}`;
    el.dataset.orderAction = action;
    el.dataset.key = key;
    el.setAttribute('aria-label', label);
    el.textContent = text;
    return el;
  };
  const render = () => {
    list.textContent = '';
    items.forEach((item) => {
      const row = document.createElement('li');
      row.className = 'order-bag__item';
      if (item.image) {
        const img = document.createElement('img');
        img.className = 'order-bag__thumb';
        img.src = item.image;
        img.alt = '';
        img.width = 72;
        img.height = 72;
        img.loading = 'lazy';
        row.appendChild(img);
      }
      const info = document.createElement('div');
      info.className = 'order-bag__info';
      const name = document.createElement('p');
      name.className = 'order-bag__name';
      name.textContent = item.name;
      const price = document.createElement('p');
      price.className = 'order-bag__price';
      price.textContent = formatOrderMoney(item.price * item.qty, format);
      const controls = document.createElement('div');
      controls.className = 'order-bag__qty';
      const qty = document.createElement('span');
      qty.className = 'order-bag__qty-value';
      qty.textContent = String(item.qty);
      qty.setAttribute('aria-label', `Quantity ${item.qty}`);
      controls.append(
        button(`Remove one ${item.name}`, 'down', item.key, '−'),
        qty,
        button(`Add one more ${item.name}`, 'up', item.key, '+'),
        button(`Remove ${item.name} from your bag`, 'remove', item.key, 'Remove'),
      );
      info.append(name, price, controls);
      row.appendChild(info);
      list.appendChild(row);
    });
    const subtotal = items.reduce((sum, item) => sum + item.price * item.qty, 0);
    if (total) total.textContent = formatOrderMoney(subtotal, format);
    if (empty) empty.hidden = items.length > 0;
    if (summary) summary.hidden = items.length === 0;
    const lines = items.map((item) => `${item.qty} x ${item.name} (${formatOrderMoney(item.price, format)} each) = ${formatOrderMoney(item.price * item.qty, format)}`);
    if (orderField) orderField.value = lines.join(' | ');
    if (bodyField) bodyField.value = `Order request from the website\n${lines.join('\n')}\nSubtotal: ${formatOrderMoney(subtotal, format)}`;
    updateBadges();
  };
  const save = () => {
    writeOrderBag(items);
    render();
  };
  const open = () => {
    if (dialog.open) return;
    returnFocus = document.activeElement;
    root.classList.add('bag-open');
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  };
  const close = () => {
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else {
      dialog.removeAttribute('open');
      dialog.dispatchEvent(new Event('close'));
    }
  };

  dialog.addEventListener('close', () => {
    root.classList.remove('bag-open');
    if (returnFocus && typeof returnFocus.focus === 'function') returnFocus.focus({ preventScroll: true });
  });
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close();
  });
  dialog.querySelectorAll('[data-order-close]').forEach((el) => el.addEventListener('click', close));

  document.addEventListener('click', (event) => {
    const add = event.target.closest('[data-order-add]');
    if (add) {
      event.preventDefault();
      const key = add.dataset.orderKey;
      const existing = items.find((item) => item.key === key);
      if (existing) existing.qty = Math.min(existing.qty + 1, 20);
      else items.push({ key, name: add.dataset.orderName, price: Number(add.dataset.orderPrice) || 0, image: add.dataset.orderImage || '', qty: 1 });
      save();
      if (main) main.hidden = false;
      if (success) success.hidden = true;
      announce(`${add.dataset.orderName} added to your bag`);
      open();
      return;
    }
    const bagLink = event.target.closest('.icon-link--cart');
    if (bagLink && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      event.preventDefault();
      open();
    }
  });

  list.addEventListener('click', (event) => {
    const control = event.target.closest('[data-order-action]');
    if (!control) return;
    const item = items.find((entry) => entry.key === control.dataset.key);
    if (!item) return;
    const action = control.dataset.orderAction;
    if (action === 'up') item.qty = Math.min(item.qty + 1, 20);
    if (action === 'down') item.qty -= 1;
    if (action === 'remove') item.qty = 0;
    items = items.filter((entry) => entry.qty > 0);
    save();
    announce(item.qty ? `${item.name}: quantity ${item.qty}` : `${item.name} removed from your bag`);
  });

  if (form) {
    form.addEventListener('submit', (event) => {
      if (!items.length) {
        event.preventDefault();
        return;
      }
      render();
      try {
        window.sessionStorage.setItem(ORDER_SENT_KEY, '1');
      } catch (error) {
        /* the thank-you panel simply won't reopen after the page reloads */
      }
    });
  }

  let sent = false;
  try {
    sent = window.sessionStorage.getItem(ORDER_SENT_KEY) === '1';
    window.sessionStorage.removeItem(ORDER_SENT_KEY);
  } catch (error) {
    sent = false;
  }
  if (success && sent) {
    items = [];
    writeOrderBag(items);
    success.hidden = false;
    if (main) main.hidden = true;
  } else if (success) {
    success.remove();
  }
  if (errors && !sent) errors.remove();
  render();
  document.querySelectorAll('[data-order-open]').forEach((el) => {
    el.hidden = false;
    el.addEventListener('click', open);
  });
  if (sent && (success || errors)) open();
  else if (document.body.classList.contains('template-cart') && items.length) open();
}

function initSmoothScroll() {
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  if (!document.body.classList.contains('smooth-on') || inEditor || reduceMotion.matches || !finePointer) return;
  const root = document.documentElement;
  root.style.scrollBehavior = 'auto';
  let target = window.scrollY;
  let current = window.scrollY;
  let running = false;
  let last = 0;
  const limit = (value) => Math.max(0, Math.min(root.scrollHeight - window.innerHeight, value));
  const canScrollInside = (node, delta) => {
    for (let el = node; el && el !== document.body && el !== root; el = el.parentElement) {
      if (el.scrollHeight <= el.clientHeight + 1) continue;
      const overflow = getComputedStyle(el).overflowY;
      if (overflow !== 'auto' && overflow !== 'scroll') continue;
      if (delta < 0 ? el.scrollTop > 0 : el.scrollTop + el.clientHeight < el.scrollHeight - 1) return true;
    }
    return false;
  };
  const step = (time) => {
    const elapsed = last ? Math.min(64, time - last) : 16;
    last = time;
    current += (target - current) * (1 - Math.exp(-elapsed / 105));
    if (Math.abs(target - current) < 0.5) {
      current = target;
      running = false;
      last = 0;
    }
    window.scrollTo(0, current);
    if (running) requestAnimationFrame(step);
  };
  const start = () => {
    if (running) return;
    running = true;
    requestAnimationFrame(step);
  };
  const stop = () => {
    running = false;
    last = 0;
    current = target = window.scrollY;
  };
  window.addEventListener('wheel', (event) => {
    if (event.ctrlKey || event.defaultPrevented || root.classList.contains('menu-open') || root.classList.contains('bag-open')) return;
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
    if (canScrollInside(event.target, event.deltaY)) return;
    event.preventDefault();
    const unit = event.deltaMode === 1 ? 40 : event.deltaMode === 2 ? window.innerHeight : 1;
    if (!running) current = target = window.scrollY;
    target = limit(target + event.deltaY * unit);
    start();
  }, { passive: false });
  window.addEventListener('scroll', () => {
    if (!running) current = target = window.scrollY;
  }, { passive: true });
  ['keydown', 'mousedown', 'touchstart'].forEach((type) => window.addEventListener(type, () => {
    if (running) stop();
  }, { passive: true }));
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href*="#"]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.hash.length < 2) return;
    const destination = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!destination) return;
    event.preventDefault();
    const offset = parseFloat(getComputedStyle(destination).scrollMarginTop) || 0;
    current = window.scrollY;
    target = limit(destination.getBoundingClientRect().top + window.scrollY - offset);
    if (window.location.hash !== url.hash) history.pushState(null, '', url.hash);
    if (!destination.hasAttribute('tabindex')) destination.setAttribute('tabindex', '-1');
    destination.focus({ preventScroll: true });
    start();
  });
}

function initMenu() {
  const menu = document.querySelector('[data-site-menu]');
  const toggles = document.querySelectorAll('[data-menu-toggle]');
  if (!menu || !toggles.length) return;
  const outside = [document.getElementById('MainContent'), document.querySelector('.site-footer')].filter(Boolean);
  let returnFocus = null;

  const setOpen = (open) => {
    menu.classList.toggle('is-open', open);
    menu.toggleAttribute('inert', !open);
    outside.forEach((el) => el.toggleAttribute('inert', open));
    document.documentElement.classList.toggle('menu-open', open);
    toggles.forEach((toggle) => {
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    });
    if (open) {
      returnFocus = document.activeElement;
      const first = menu.querySelector('a');
      if (first) first.focus({ preventScroll: true });
    } else if (returnFocus) {
      returnFocus.focus({ preventScroll: true });
      returnFocus = null;
    }
  };

  toggles.forEach((toggle) => toggle.addEventListener('click', () => setOpen(!menu.classList.contains('is-open'))));
  menu.addEventListener('click', (event) => {
    if (event.target.closest('a')) setOpen(false);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && menu.classList.contains('is-open')) setOpen(false);
  });
  window.matchMedia('(min-width: 750px)').addEventListener('change', (event) => {
    if (event.matches && menu.classList.contains('is-open')) setOpen(false);
  });
}

const portraitQuery = window.matchMedia('(max-width: 749px)');

function loadHeroSources(video) {
  if (!video.hasAttribute('data-hero-sources')) return false;
  const data = video.dataset;
  const shape = portraitQuery.matches && (data.portraitHls || data.portraitMp4) ? 'portrait' : 'landscape';
  if (data.shape === shape) return false;
  data.shape = shape;
  const wide = window.matchMedia('(min-width: 1600px)').matches;
  const mp4 = shape === 'landscape' && wide && data.landscapeMp4Hd ? data.landscapeMp4Hd : data[`${shape}Mp4`];
  video.textContent = '';
  [[data[`${shape}Hls`], 'application/vnd.apple.mpegurl'], [mp4, 'video/mp4']].forEach(([url, type]) => {
    if (!url) return;
    const source = document.createElement('source');
    source.src = url;
    source.type = type;
    video.appendChild(source);
  });
  video.load();
  return true;
}

function initHeroVideo(root = document) {
  root.querySelectorAll('[data-hero]').forEach((hero) => {
    const wrap = hero.querySelector('.marlow-hero__video-wrap');
    const video = wrap ? wrap.querySelector('video') : null;
    const button = hero.querySelector('[data-hero-pause]');
    if (!video || video.dataset.ready) return;
    video.dataset.ready = 'true';
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.removeAttribute('controls');

    const saveData = Boolean(navigator.connection && navigator.connection.saveData);
    let pausedByVisitor = reduceMotion.matches || saveData;
    const setButton = () => {
      if (!button) return;
      button.setAttribute('aria-pressed', String(pausedByVisitor));
      button.setAttribute('aria-label', pausedByVisitor ? 'Play background video' : 'Pause background video');
    };
    const play = () => {
      loadHeroSources(video);
      const attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(() => {});
    };

    video.addEventListener('playing', () => wrap.classList.add('is-playing'));
    portraitQuery.addEventListener('change', () => {
      if (!video.dataset.shape) return;
      wrap.classList.remove('is-playing');
      loadHeroSources(video);
      if (!pausedByVisitor) play();
    });
    setButton();
    if (!pausedByVisitor) play();

    if (button) {
      button.addEventListener('click', () => {
        pausedByVisitor = !pausedByVisitor;
        if (pausedByVisitor) video.pause();
        else play();
        setButton();
      });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) video.pause();
        else if (!pausedByVisitor) play();
      }).observe(hero);
    }
  });
}

const sceneQuery = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
let unicornSdk;

function loadUnicornSdk() {
  if (window.UnicornStudio && typeof window.UnicornStudio.init === 'function') return Promise.resolve(window.UnicornStudio);
  if (!unicornSdk) {
    unicornSdk = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/gh/hiunicornstudio/unicornstudio.js@v2.4.3/dist/unicornStudio.umd.js';
      script.async = true;
      script.onload = () => {
        if (window.UnicornStudio && typeof window.UnicornStudio.init === 'function') resolve(window.UnicornStudio);
        else reject(new Error('Unicorn Studio unavailable'));
      };
      script.onerror = () => reject(new Error('Unicorn Studio failed to load'));
      document.head.appendChild(script);
    });
  }
  return unicornSdk;
}

function heroSettled(layer) {
  const hero = layer.closest('[data-hero]');
  const image = hero ? hero.querySelector('.marlow-hero__image') : null;
  const running = image && typeof image.getAnimations === 'function'
    ? image.getAnimations().filter((animation) => animation.timeline === document.timeline)
    : [];
  if (!running.length) return Promise.resolve();
  return Promise.race([
    Promise.all(running.map((animation) => animation.finished)).catch(() => {}),
    new Promise((resolve) => setTimeout(resolve, 3200)),
  ]);
}

function initUnicornStudio() {
  const layers = Array.from(document.querySelectorAll('[data-unicorn-scene]:not(.is-ready)'));
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);
  if (!layers.length || reduceMotion.matches || !sceneQuery.matches || saveData) return;
  loadUnicornSdk()
    .then((studio) => studio.init())
    .then(() => Promise.all(layers.map(heroSettled)))
    .then(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        layers.forEach((layer) => layer.classList.add('is-ready'));
      }));
    })
    .catch(() => {});
}

function initProductGallery() {
  const mainProductImage = document.getElementById('MainProductImage');
  document.querySelectorAll('[data-product-thumbnail]').forEach((thumbnail) => {
    thumbnail.addEventListener('click', () => {
      if (!mainProductImage) return;
      mainProductImage.src = thumbnail.dataset.imageUrl;
      mainProductImage.srcset = '';
      if (thumbnail.dataset.imageAlt) mainProductImage.alt = thumbnail.dataset.imageAlt;
      document.querySelectorAll('[data-product-thumbnail]').forEach((item) => {
        item.classList.remove('is-active');
        item.setAttribute('aria-pressed', 'false');
      });
      thumbnail.classList.add('is-active');
      thumbnail.setAttribute('aria-pressed', 'true');
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initHeroHeader();
  initHeader();
  initMenu();
  initReveal();
  initHeroVideo();
  initUnicornStudio();
  initProductGallery();
  initScrollProgress();
  initBreedIndex();
  initSmoothScroll();
  initOrderBag();
  initMegaMenu();
  initHeaderHide();
  initCardGalleries();
});

document.addEventListener('shopify:section:load', (event) => {
  initHeroHeader();
  initReveal(event.target);
  initHeroVideo(event.target);
  initUnicornStudio();
  initScrollProgress(event.target);
  initBreedIndex(event.target);
  initMegaMenu(event.target);
  initCardGalleries(event.target);
  if (event.target.querySelector('[data-site-header]')) {
    initHeader();
    initMenu();
  }
});

['shopify:block:select', 'shopify:block:deselect'].forEach((type) => {
  document.addEventListener(type, (event) => {
    const item = event.target.closest('[data-mega]');
    if (item) item.dispatchEvent(new CustomEvent('mega:toggle', { detail: type === 'shopify:block:select' }));
  });
});

document.addEventListener('shopify:section:unload', (event) => {
  if (!event.target.querySelector('[data-unicorn-scene]')) return;
  if (window.UnicornStudio && typeof window.UnicornStudio.destroy === 'function') window.UnicornStudio.destroy();
});
